import { getBrandFromHostname, theme } from '@arnold/common';
import pulserMark from '@arnold/common/lib/assets/icons/categories/pulser.svg';
import styled from '@emotion/styled';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { cookieConsentManager } from '../../../components/CookieConsent';

const BRAND_LOGOS = {
  arnold: { name: 'Arnold', mark: '/arnold-icon.svg', aspectRatio: '32 / 24' },
  pulser: { name: 'Pulser', mark: pulserMark, aspectRatio: '24 / 23' },
} as const;
// Resolved once like the theme itself: the brand only changes with the hostname, which needs a reload anyway.
const brandLogo = BRAND_LOGOS[getBrandFromHostname(window.location.hostname, window.location.search)];

/** A single quiet, centred line closing the report; the brand colour only shows on hover. */
export const ReportFooter: FC = () => {
  const { t } = useTranslation('newReport');
  const { t: tReport } = useTranslation('Report');

  return (
    <Root>
      <Row>
        <Group>
          <Brand>
            <Mark src={brandLogo.mark} alt="" />
            {brandLogo.name}
          </Brand>
          <Dot aria-hidden={true} />
          <span>{t('footer.copyright', { year: new Date().getFullYear() })}</span>
        </Group>
        <Dot aria-hidden={true} />
        <Links>
          <Link href={`mailto:${tReport('arnoldHelpMeEmail')}`}>{tReport('arnoldHelpMeEmail')}</Link>
          <Link href={tReport('productTermsUrl')} target="_blank" rel="noreferrer">
            {t('footer.termsOfService')}
          </Link>
          <Link href={tReport('privacyPolicyUrl')} target="_blank" rel="noreferrer">
            {t('footer.privacyPolicy')}
          </Link>
          <Link as="button" type="button" onClick={() => cookieConsentManager?.showPreferences()}>
            {t('footer.cookieSettings')}
          </Link>
        </Links>
      </Row>
    </Root>
  );
};

const Root = styled.footer`
  padding: ${theme.spacing.f} 0;
  border-top: 1px solid ${theme.colors.borderSeparator.default};
  background-color: ${theme.colors.backgroundCover.default};
  color: ${theme.colors.text.secondary};
  font-family: ${theme.typography.body.small.regular.family};
  font-size: ${theme.typography.body.small.regular.fontSize};
  line-height: ${theme.typography.body.small.regular.lineHeight};
`;

/** Centres the content in a column as wide as the report body above it. */
const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.e} ${theme.spacing.f};
  max-width: calc(1000px + 2 * ${theme.spacing.g});
  margin: 0 auto;
  padding: 0 ${theme.spacing.g};
  text-align: center;

  @media (max-width: ${theme.breakpoint.small}) {
    padding: 0 ${theme.spacing.f};
  }
`;

const Group = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.d};
`;

const Brand = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.c};
  color: ${theme.colors.text.primary};
  font-family: ${theme.typography.heading.xLarge.default.family};
  font-weight: ${theme.typography.heading.xLarge.default.fontWeight};
  font-size: ${theme.typography.body.medium.regular.fontSize};
  line-height: 1;
  margin-top: -4px;
`;

const Mark = styled.img`
  display: block;
  flex-shrink: 0;
  height: 18px;
  margin-bottom: 5px;
  aspect-ratio: ${brandLogo.aspectRatio};
`;

const Dot = styled.span`
  width: 3px;
  height: 3px;
  margin: auto 0;
  border-radius: 50%;
  background-color: ${theme.colors.text.disabled};

  @media (max-width: ${theme.breakpoint.small}) {
    display: none;
  }
`;

const Links = styled.nav`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${theme.spacing.f};
`;

// The cookie preferences open in place rather than navigating, so that entry renders as a button styled the same.
const Link = styled.a`
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover,
  &:focus-visible {
    color: ${theme.colors.brand.primary};
    text-decoration: none;
  }
`;
